import DOMPurify from 'dompurify';
import { SANITIZE_CONFIG, SANITIZE_ALLOWED_STYLES } from '../constants';

// A separate DOMPurify instance, so the style hook below doesn't change how
// other DOMPurify calls in the app (e.g. the paste cleaning) behave.
const purify = DOMPurify(window);

// Keeps only SANITIZE_ALLOWED_STYLES in each element's inline style. DOMPurify
// itself can only keep or remove the whole style attribute, which would also
// lose the toolbar's colors and alignment.
//   <p style="color:#ff0000;position:fixed;inset:0">  ->  <p style="color: rgb(255, 0, 0);">
//   <div style="margin-top:-2000px">  ->  <div>
purify.addHook('afterSanitizeAttributes', (node) => {
  if (!node.hasAttribute?.('style')) return;
  const kept = node.style
    ? SANITIZE_ALLOWED_STYLES.map((name) => [name, node.style.getPropertyValue(name)]).filter(
        ([, value]) => value,
      )
    : [];
  node.removeAttribute('style');
  kept.forEach(([name, value]) => node.style.setProperty(name, value));
});

/**
 * Reduces summary HTML to text formatting: only the tags and attributes in
 * SANITIZE_CONFIG are kept (no media, forms, buttons or <style> blocks),
 * DOMPurify removes scripts, event handlers such as onerror and javascript:
 * links, links that are not https:// lose their href, and the hook above
 * removes every inline style outside SANITIZE_ALLOWED_STYLES. Used wherever
 * summary HTML is shown, copied, loaded into the editor or saved.
 *   <p onclick="steal()" style="position:fixed">Hi<img src=x onerror="steal()"></p>  ->  <p>Hi</p>
 * @param {String} html The summary HTML
 * @param {Object} [options] Extra DOMPurify options, e.g. { RETURN_DOM_FRAGMENT: true }
 * @returns {String|DocumentFragment} The sanitized HTML, or a fragment when requested in options
 */
export const sanitizeSummaryHtml = (html, options) =>
  purify.sanitize(html ?? '', { ...SANITIZE_CONFIG, ...options });
